import React from 'react';
import { I18nextProvider } from 'react-i18next';
import { cacheExchange, createClient, errorExchange, fetchExchange, Provider } from 'urql';
import BookingWidget from './components/BookingWidget';
import i18n from './i18n/config';
import { AppError, ErrorBoundaryProvider, ErrorTypes } from './provider/ErrorBoundaryProvider';
import { ToastProvider } from './provider/ToastManager';
import LoadingComponent from './shared/components/LoadingComponent';
import useWidgetConfiguration from './shared/hooks/useWidgetConfiguration';
import getApiUrl from './utils/getApiUrl';

const isLocalhost = window.location.hostname.indexOf('localhost') !== -1 ||
                    window.location.hostname.indexOf('.local') !== -1;

const apiUrl = getApiUrl();

//@ts-ignore
const handleGraphQLErrors = ({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    //@ts-ignore
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`
      );

      let errorMessage;
      switch (extensions?.code) {
        case 'FORBIDDEN':
          errorMessage = 'You do not have permission to perform this action';
          break;
        case 'BAD_USER_INPUT':
          errorMessage = 'Invalid input provided';
          break;
        default:
          errorMessage = 'An unexpected error occurred';
      }

      // Instead of throwing, we'll handle the error
      const error = new AppError(ErrorTypes.GRAPHQL, errorMessage, networkError);
      window.dispatchEvent(new CustomEvent('app-error', { detail: error }));
    });
  }

  if (networkError) {
    const error = new AppError(
      ErrorTypes.GRAPHQL,
      'Network error, check internet connection',
      networkError
    );
    window.dispatchEvent(new CustomEvent('app-error', { detail: error }));
  }
};

const client = createClient({
  url: apiUrl,
  exchanges: [
    errorExchange({
      //@ts-ignore
      onError: handleGraphQLErrors
    }),
    cacheExchange,
    fetchExchange
  ],
  ...(isLocalhost && {
    fetchOptions: {
      headers: {
        'X-API-Key': 'Bearer f0a70681a4ccd1288f3fefd068187a402969dc7da7b8da879739fcec1fe63075'
      }
    }
  })
});

const WidgetWithConfiguration: React.FC = () => {
  // Now the hook is used inside the Provider
  const { widgetConfiguration, isLoading, bookingPageConfiguration } = useWidgetConfiguration();
  if (isLoading) {
    return <LoadingComponent />
  }

  return (
    <BookingWidget
      widgetConfiguration={widgetConfiguration}
      bookingPageConfiguration={bookingPageConfiguration}
    />
  );
};

const App: React.FC = () => {

  return (
    <I18nextProvider i18n={i18n}>
      <ToastProvider>
        <ErrorBoundaryProvider>
          <Provider value={client}>
            <WidgetWithConfiguration />
          </Provider>
        </ErrorBoundaryProvider>
      </ToastProvider>
    </I18nextProvider>
  );
};

export default App;
